import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import translations from "@/constants/translations";
import { HEADER } from "@/constants/testIds";
import { Menu, X, Calendar } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const getImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}/api/files/${path}`;
};

export default function Header({ settings }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, toggle } = useLanguage();
  const t = translations[lang];

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const logoSrc = settings?.logo_header ? getImageUrl(settings.logo_header) : "";
  const reserveUrl = settings?.reservation_url || "#";

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/98 shadow-sm backdrop-blur-xl" : "bg-white/92 backdrop-blur-xl"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
          {/* Logo */}
          <a href="/" className="flex-shrink-0" data-testid={HEADER.logo}>
            {logoSrc ? (
              <img
                src={logoSrc}
                alt="O Quinto Império"
                className="h-9 md:h-11 w-auto object-contain"
              />
            ) : (
              <span className="font-cormorant text-xl font-semibold text-[#4A4A4A]">
                O Quinto Império
              </span>
            )}
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a
              href="/"
              data-testid={HEADER.navHome}
              className="font-lato text-sm text-[#4A4A4A] hover:text-[#0077B6] transition-colors duration-200 tracking-wider uppercase"
            >
              {t.nav.home}
            </a>
            <a
              href="/menu"
              data-testid={HEADER.navMenu}
              className="font-lato text-sm text-[#4A4A4A] hover:text-[#0077B6] transition-colors duration-200 tracking-wider uppercase"
            >
              {t.nav.menu}
            </a>
            <a
              href="/#location"
              data-testid={HEADER.navLocation}
              className="font-lato text-sm text-[#4A4A4A] hover:text-[#0077B6] transition-colors duration-200 tracking-wider uppercase"
            >
              {t.nav.location}
            </a>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {/* Language toggle */}
            <button
              onClick={toggle}
              data-testid={HEADER.langToggle}
              className="hidden md:flex items-center gap-1 font-lato text-xs tracking-widest text-[#4A4A4A] hover:text-[#0077B6] transition-colors duration-200 border border-[#4A4A4A]/30 hover:border-[#0077B6] px-2 py-1 rounded"
            >
              {lang === "pt" ? "EN" : "PT"}
            </button>

            {/* Reserve button */}
            <a
              href={reserveUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={HEADER.reserveBtn}
              className="hidden md:inline-flex items-center gap-2 px-5 py-2 bg-[#0077B6] text-white font-lato text-xs tracking-widest uppercase hover:bg-[#005f8f] transition-colors duration-200"
            >
              <Calendar size={14} />
              {t.nav.reserve}
            </a>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid={HEADER.mobileMenuBtn}
              className="md:hidden text-[#4A4A4A] p-1"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-6 py-5 flex flex-col gap-4">
              <a
                href="/"
                onClick={() => setMobileOpen(false)}
                className="font-lato text-sm text-[#4A4A4A] tracking-wider uppercase py-1"
              >
                {t.nav.home}
              </a>
              <a
                href="/menu"
                onClick={() => setMobileOpen(false)}
                className="font-lato text-sm text-[#4A4A4A] tracking-wider uppercase py-1"
              >
                {t.nav.menu}
              </a>
              <a
                href="/#location"
                onClick={() => setMobileOpen(false)}
                className="font-lato text-sm text-[#4A4A4A] tracking-wider uppercase py-1"
              >
                {t.nav.location}
              </a>
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <button
                  onClick={() => { toggle(); setMobileOpen(false); }}
                  data-testid={HEADER.langToggle + "-mobile"}
                  className="font-lato text-xs text-[#4A4A4A] tracking-widest border border-[#4A4A4A]/30 px-3 py-1.5 rounded"
                >
                  {lang === "pt" ? "EN" : "PT"}
                </button>
                <a
                  href={reserveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-5 py-2 bg-[#0077B6] text-white font-lato text-xs tracking-widest uppercase"
                >
                  <Calendar size={13} />
                  {t.nav.reserve}
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Floating CTA — mobile always visible */}
      <a
        href={reserveUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-reserve-btn"
        className="fixed bottom-5 right-5 z-40 md:hidden bg-[#0077B6] text-white px-4 py-3 rounded-full shadow-xl flex items-center gap-2 font-lato text-xs uppercase tracking-wider hover:bg-[#005f8f] transition-colors duration-200"
      >
        <Calendar size={15} />
        {t.nav.reserve}
      </a>
    </>
  );
}
